<template>
  <div>
    <div class="f-flex f-justify-between">
      <BaseRadio
        ref="radio"
        name="shipping_method"
        :value="shippingMethod.shippingMethodResource.id"
        :id="$id('shipping-' + shippingMethod.shippingMethodResource.id)"
        @input="shippingMethodChanged"
        v-model="selected"
      >
        <span
          :aria-label="`${shippingMethod.shippingMethodResource.name}, cena: ${price}`"
        >
          {{ shippingMethod.shippingMethodResource.name }}
        </span>
        <ShippingPopover
          v-if="shippingDescription"
          :description="shippingDescription"
        ></ShippingPopover>
        <div v-show="freeOfCharge" class="f-text-sm">
          {{ formattedFreeOfCharge(freeOfCharge) }}
        </div>
      </BaseRadio>
      <div v-if="this.freeShipping">
        <div class="f-text-right f-text-xs f-line-through">
          {{ price }}
        </div>
        <div
          class="f-text-right"
          v-bind:class="{ 'f-text-red-600': this.freeShipping }"
        >
          {{ $t("prices.free").toUpperCase() }}
        </div>
      </div>
      <div v-if="!this.freeShipping">
        {{ price }}
      </div>
    </div>
    <ValidationProvider
      slim
      :rules="rulesObject"
      :skipIfEmpty="false"
      v-slot="{ errors }"
    >
      <div v-show="isSelected">
        <label>
          <select
            class="f-form-select f-border-gray-300 f-rounded f-w-full f-mt-2 f-py-1 f-text-gray-700"
            :class="{ 'f-border-red-600': getFirstError(errors) }"
            :id="
              $id('shipping-select-' + shippingMethod.shippingMethodResource.id)
            "
            :name="
              shippingMethod.shippingMethodResource.id + '_shipping_address'
            "
            v-model="selectedOption"
            @change="shippingMethodChanged"
          >
            <option
              v-for="option in options"
              :value="option.value"
              :key="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </label>
        <div v-if="getFirstError(errors)" class="f-text-sm f-text-red-600">
          {{ getFirstError(errors) }}
        </div>
      </div>
    </ValidationProvider>
  </div>
</template>

<script lang="ts">
import FormShippingItem from "@/module/FormShippingItem";
import FormResource from "@/module/Form/Resources/FormResource";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import mixins from "vue-typed-mixins";
import money from "@/module/Filters/MoneyFilter";
import { PropType } from "vue";
import {
  isObjectOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import { ValidationProvider } from "vee-validate";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import ShippingPopover from "@/components/Form/ShippingMethods/ShippingPopover.vue";
import OrderPrices from "@/module/Order/OrderPrices";
import NumberHelper from "@/module/NumberHelper";
import SelectedShippingMethod from "@/module/Shipping/SelectedShippingMethod";

interface ComponentData {
  selected: string | null;
  selectedOption: string | null;
  showShippingInfo: boolean;
  freeShipping: boolean;
}

export default mixins(InputValidationMixin).extend({
  name: "PersonalCollection",

  components: {
    ShippingPopover,
    BaseRadio,
    ValidationProvider,
  },

  props: {
    shippingMethod: {
      type: Object as PropType<FormShippingItem>,
      required: true,
    },
    currentlySelectedRadio: {
      type: null as unknown as PropType<SelectedShippingMethod | null>,
      required: true,
      validator: isObjectOrNull,
    },
    currency: {
      type: String,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    totalPriceIncVat: {
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: null,
      selectedOption: "",
      showShippingInfo: false,
      freeShipping: false,
    };
  },

  computed: {
    isSelected(): boolean {
      return (
        this.shippingMethod.shippingMethodResource.id ===
        this.currentlySelectedRadio?.shipping_method_id
      );
    },

    rulesObject(): object {
      return this.isSelected ? { required: true } : {};
    },

    freeOfCharge(): number | null {
      if (this.shippingMethod.freeOfCharges.hasOwnProperty("noFreeShipping")) {
        return null;
      }

      const price = this.shippingMethod.freeOfCharges[this.currency];
      const includingVat =
        this.shippingMethod.shippingMethodResource.including_vat;
      const currentVat = this.shippingMethod.formItems[0].currentVat;

      if (this.formResource.show_prices_including_vat) {
        return NumberHelper.priceIncludingVat(price, currentVat, includingVat);
      }
      return NumberHelper.priceWithOutVat(price, currentVat, includingVat);
    },

    price(): string {
      let min: number | null = null;
      let max: number | null = null;

      if (this.freeOfCharge !== null) {
        this.updateFreeShipping(this.isEligibleForFreeShipping());
      }

      for (const formItem of this.shippingMethod.formItems) {
        let price: number;
        if (this.formResource.show_prices_including_vat) {
          price = formItem.currentPriceIncVat;
        } else {
          price = formItem.currentPrice;
        }

        if (min === null || price <= min) {
          min = price;
        }

        if (max === null || price >= max) {
          max = price;
        }
      }

      if (min === max) {
        return money(Number(min), this.$root.$i18n);
      }

      return (
        money(Number(min), this.$root.$i18n) +
        " - " +
        money(Number(max), this.$root.$i18n)
      );
    },

    shippingDescription(): string | null {
      return this.shippingMethod.shippingMethodResource.description;
    },

    options(): { value: string | null; text: string }[] {
      const options = [];

      options.push({
        value: "",
        text: String(this.$t("shipping_method.personal_collection.prompt")),
      });

      for (const formItem of this.shippingMethod.formItems) {
        let price: number;
        if (this.formResource.show_prices_including_vat) {
          price = formItem.currentPriceIncVat;
        } else {
          price = formItem.currentPrice;
        }

        options.push({
          value: formItem.name,
          text: formItem.name + " (" + this.$n(price, "currency") + ")",
        });
      }

      return options;
    },
  },

  methods: {
    formattedFreeOfCharge(price: number): string {
      if (price) {
        return (
          String(this.$t("shipping_method.free_shipping_from")) +
          money(price, this.$root.$i18n)
        );
      }
      return "";
    },

    updateFreeShipping(value: boolean): void {
      this.freeShipping = value;
    },

    triggerVisibility(): void {
      this.$emit("shippingMethodVisibilityChanged", { visible: true });
    },

    isEligibleForFreeShippingHelper(vat: boolean): number {
      if (vat) {
        return this.order.totalPriceWoShippingIncludingVat;
      }
      return this.order.totalPriceWoShipping;
    },

    isEligibleForFreeShipping(): boolean {
      if (this.freeOfCharge !== null) {
        if (
          this.freeOfCharge <=
          this.isEligibleForFreeShippingHelper(
            this.formResource.show_prices_including_vat,
          )
        ) {
          return true;
        }
      }
      return false;
    },

    shippingMethodChanged(): void {
      this.$emit("shippingMethodChanged", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        shipping_metadata: {
          address: {
            simple_address: this.selectedOption,
          },
        },
        shipping_form_item: this.shippingMethod.formItems.find((item) => {
          return item.name === this.selectedOption;
        }),
      } as SelectedShippingMethod);
      this.$emit("isEligibleForFreeShipping", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
      });
    },
  },

  watch: {
    totalPriceIncVat(): void {
      if (this.selected) {
        this.$emit("isEligibleForFreeShipping", {
          shipping_method_id: this.shippingMethod.shippingMethodResource.id,
          is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
        });
      }
    },

    country(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
      this.selectedOption = null;
    },
  },

  mounted() {
    this.$emit("shippingMethodVisibilityChanged", { visible: true });
  },
});
</script>

<style lang="scss"></style>
